import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';

const STEPS = [
  { num: 1, label: '영상 업로드' },
  { num: 2, label: 'STT 추출' },
  { num: 3, label: '편집' },
  { num: 4, label: '1차 영상 생성' },
  { num: 5, label: 'Gemini 분석' },
  { num: 6, label: '최종 영상' },
];

function statusToStep(status) {
  if (!status) return 1;
  const map = {
    step1: 1,
    step2_ready: 2, step2_processing: 2,
    step3: 3, step4: 3,
    step5_processing: 4, step5: 4,
    step6: 4, step7_ready: 5, step7_processing: 5, step7: 5, step7_editing: 5,
    step8_processing: 6, step8: 6, completed: 6,
  };
  return map[status] || 1;
}

function formatTime(seconds) {
  if (seconds == null || isNaN(seconds)) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return seconds < 0 ? '0분 0초' : '-';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}분 ${s}초`;
}

function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

const DIRECTIVE_COLORS = {
  asset: { bg: '#4CAF5018', border: '#4CAF5044', color: '#4CAF50', icon: '📷', label: '자료화면' },
  effect: { bg: '#2196F318', border: '#2196F344', color: '#2196F3', icon: '✨', label: '텍스트효과' },
  bgm: { bg: '#9C27B018', border: '#9C27B044', color: '#9C27B0', icon: '🎵', label: 'BGM' },
};

const STAGE_LABELS = {
  subtitle: '자막 번인',
  assets: '자료화면 삽입',
  highlights: '강조 텍스트',
  bgm: 'BGM 믹싱',
  color: '색보정',
  intro_outro: '인트로/아웃트로',
  concat: '최종 합성',
};

export default function ProjectEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { addToast } = useToast();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  // STEP 1: 청크 업로드 상태
  // uploadPhase: 'idle' | 'selected' | 'uploading' | 'completing' | 'done' | 'resume'
  const [uploadPhase, setUploadPhase] = useState('idle');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({ speed: 0, uploaded: 0, total: 0, chunksDone: 0, chunksTotal: 0 });
  const [resumeInfo, setResumeInfo] = useState(null);
  const uploadAbortRef = useRef(false);
  const [sttProgress, setSttProgress] = useState(null);
  const [sttRunning, setSttRunning] = useState(false);
  const [sttSegments, setSttSegments] = useState([]);
  const sttScrollRef = useRef(null);
  const [transcript, setTranscript] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [trimStart, setTrimStart] = useState(0);
  const isUserEditing = useRef(false);
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const listRef = useRef(null);
  const userInteractingRef = useRef(false);
  const [highlightedSegIndex, setHighlightedSegIndex] = useState(null);
  const fixedTimelineRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const [tlPixelsPerSec, setTlPixelsPerSec] = useState(20);
  const tlScrollRef = useRef(null);
  const stopAtRef = useRef(null);
  const canvasRef = useRef(null);
  const waveformRef = useRef(null); // 파형 데이터 캐시
  const [waveformLoaded, setWaveformLoaded] = useState(false);
  const animFrameRef = useRef(null); // requestAnimationFrame
  const [cutProgress, setCutProgress] = useState(null);
  const [cutRunning, setCutRunning] = useState(false);
  const editedVideoRef = useRef(null);
  const [step7Progress, setStep7Progress] = useState(null);
  const [step7Running, setStep7Running] = useState(false);
  const [secondTranscript, setSecondTranscript] = useState([]);
  const [geminiAnalysis, setGeminiAnalysis] = useState(null);
  const [step8Progress, setStep8Progress] = useState(null);
  const [step8Running, setStep8Running] = useState(false);
  const [step8Stages, setStep8Stages] = useState({});
  const finalVideoRef = useRef(null);
  const fileInputRef = useRef(null);
  const scriptInputRef = useRef(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [geminiRetryRunning, setGeminiRetryRunning] = useState(false);
  const [geminiAnalysisFailed, setGeminiAnalysisFailed] = useState(false);
  const [scriptUploaded, setScriptUploaded] = useState(false);
  const [scriptPreview, setScriptPreview] = useState('');
  const [subtitles, setSubtitles] = useState([]);
  const [activeTab, setActiveTab] = useState('script');

  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/v3/project/${id}`);
      const p = data.project;
      setProject(p);

      const step = statusToStep(p.status);
      setCurrentStep(step);

      // 상태별 UI 복원
      // 대본 상태 복원
      if (p.scriptText) {
        setScriptUploaded(true);
        setScriptPreview(p.scriptText.slice(0, 200));
      }

      // Gemini 분석 실패 상태 복원
      switch (p.status) {
        case 'step1':
          setUploadPhase('idle');
          break;
        case 'step2_ready':
          setUploadPhase('done');
          break;
        case 'step2_processing':
          setUploadPhase('done');
          break;
        case 'step3':
        case 'step4':
          if (p.transcript?.length > 0) setTranscript(p.transcript);
          break;
        case 'step5':
        case 'step5_processing':
        case 'step6':
          if (p.transcript?.length > 0) setTranscript(p.transcript);
          break;
        case 'step7':
        case 'step7_ready':
        case 'step7_editing':
          if (p.transcript?.length > 0) setTranscript(p.transcript);
          if (p.geminiAnalysis) setGeminiAnalysis(p.geminiAnalysis);
          if (p.secondTranscript?.length > 0) setSecondTranscript(p.secondTranscript);
          break;
        case 'step8':
        case 'step8_processing':
        case 'completed':
          if (p.transcript?.length > 0) setTranscript(p.transcript);
          if (p.geminiAnalysis) setGeminiAnalysis(p.geminiAnalysis);
          if (p.secondTranscript?.length > 0) setSecondTranscript(p.secondTranscript);
          break;
        default:
          break;
      }
    } catch (err) {
      addToast('프로젝트를 불러올 수 없습니다', 'error');
      navigate('/v3/project');
    } finally {
      setLoading(false);
    }
  }, [api, id, addToast, navigate]);

  useEffect(() => { loadProject(); }, [loadProject]);

  // ─── 상태 폴링 (SSE 끊김/새로고침 백업) ───
  useEffect(() => {
    const processingStatuses = [
      'step2_processing', 'step5_processing', 'step7_processing', 'step8_processing'
    ];
    if (!project?.status || !processingStatuses.includes(project.status)) return;

    const token = localStorage.getItem('accessToken');
    const poller = setInterval(async () => {
      try {
        const res = await fetch(`/api/v3/project/${id}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();

        // 처리 완료 감지 → 자동 전환
        if (data.status === 'step3' && currentStep === 2) {
          clearInterval(poller);
          await loadProject();
          setCurrentStep(3);
        }
        if (data.status === 'step6' && currentStep === 4) {
          clearInterval(poller);
          setProject(prev => ({ ...prev, status: 'step6', editedVideo: data.editedVideo }));
          setCurrentStep(5);
        }
        if (data.status === 'step7' && currentStep === 5 && step7Running) {
          clearInterval(poller);
          await loadProject();
        }
        if ((data.status === 'step8' || data.status === 'completed') && step8Running) {
          clearInterval(poller);
          setProject(prev => ({ ...prev, status: data.status, finalVideo: data.finalVideo }));
          setCurrentStep(6);
          setStep8Running(false);
        }
      } catch {}
    }, 3000);

    return () => clearInterval(poller);
  }, [project?.status, currentStep, id, step7Running, step8Running, loadProject]);

  // ─── SSE helper (POST + ReadableStream) ───
  const runSSE = async (url, { onProgress, onDone, onError, onSegment, onStage, onReview }) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      onError?.({ message: err.error || err.message || `HTTP ${response.status}` });
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'progress') onProgress?.(data);
          else if (data.type === 'segment') onSegment?.(data);
          else if (data.type === 'stage') onStage?.(data);
          else if (data.type === 'done') onDone?.(data);
          else if (data.type === 'review') onReview?.(data);
          else if (data.type === 'error') onError?.(data);
          else if (data.type === 'gemini_error') onProgress?.({ percent: 95, message: data.message });
        } catch {}
      }
    }
  };

  // ─── STEP 1: 청크 업로드 ───
  const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
  const PARALLEL = 3;

  // 유틸
  const formatSpeed = (bps) => {
    if (bps > 1024 * 1024 * 1024) return `${(bps / 1024 / 1024 / 1024).toFixed(1)} GB/s`;
    if (bps > 1024 * 1024) return `${(bps / 1024 / 1024).toFixed(1)} MB/s`;
    if (bps > 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
    return `${Math.round(bps)} B/s`;
  };

  const formatSize = (b) => {
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)}GB`;
    if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)}MB`;
    if (b >= 1024) return `${(b / 1024).toFixed(0)}KB`;
    return `${b}B`;
  };

  // 페이지 로드 시 이어받기 확인
  useEffect(() => {
    if (!project || project.status !== 'step1') return;
    (async () => {
      try {
        const { data } = await api.get(`/v3/project/${id}/upload/status`);
        if (data.hasSession && data.uploadedChunks.length > 0 && data.uploadedChunks.length < data.totalChunks) {
          setResumeInfo(data);
          setUploadPhase('resume');
        }
      } catch {}
    })();
  }, [project?.status, api, id]);

  // 파일 선택 → 'selected' 상태
  const handleFileSelect = (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['mp4', 'mov'].includes(ext)) { addToast('MP4 또는 MOV 파일만 업로드 가능합니다', 'error'); return; }
    setSelectedFile(file);
    setUploadPhase('selected');
  };

  // 업로드 시작 (신규 또는 이어받기)
  const startUpload = async (resume = false, fileOverride = null) => {
    const file = fileOverride || selectedFile;
    if (!file) return;
    if (fileOverride) setSelectedFile(fileOverride);

    setUploadPhase('uploading');
    setUploadProgress(0);
    uploadAbortRef.current = false;

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uid;
    let alreadyDone = new Set();

    try {
      if (resume && resumeInfo) {
        uid = resumeInfo.uploadId;
        alreadyDone = new Set(resumeInfo.uploadedChunks);
      } else {
        const { data } = await api.post(`/v3/project/${id}/upload/init`, {
          filename: file.name, fileSize: file.size, totalChunks, chunkSize: CHUNK_SIZE,
        });
        uid = data.uploadId;
      }
      setUploadId(uid);

      // 미완료 청크
      const pending = [];
      for (let i = 0; i < totalChunks; i++) {
        if (!alreadyDone.has(i)) pending.push(i);
      }

      let done = alreadyDone.size;
      let bytes = done * CHUNK_SIZE;
      const samples = []; // { t, b }

      const tick = () => {
        const now = Date.now();
        samples.push({ t: now, b: bytes });
        while (samples.length > 1 && samples[0].t < now - 5000) samples.shift();
        let speed = 0;
        if (samples.length >= 2) {
          const dt = (samples[samples.length - 1].t - samples[0].t) / 1000;
          if (dt > 0) speed = (samples[samples.length - 1].b - samples[0].b) / dt;
        }
        setUploadProgress(Math.round((done / totalChunks) * 100));
        setUploadStats({ speed, uploaded: bytes, total: file.size, chunksDone: done, chunksTotal: totalChunks });
      };
      tick();

      // 청크 1개 업로드
      const sendChunk = async (ci) => {
        if (uploadAbortRef.current) return;
        const start = ci * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append('uploadId', uid);
        fd.append('chunkIndex', String(ci));
        fd.append('chunk', blob, `chunk_${ci}`);
        await api.post(`/v3/project/${id}/upload/chunk`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        done++;
        bytes += (end - start);
        tick();
      };

      // 3개씩 병렬
      for (let i = 0; i < pending.length; i += PARALLEL) {
        if (uploadAbortRef.current) throw new Error('cancelled');
        await Promise.all(pending.slice(i, i + PARALLEL).map(sendChunk));
      }
      if (uploadAbortRef.current) throw new Error('cancelled');

      // 서버 합치기 단계
      setUploadPhase('completing');
      setUploadProgress(100);

      const { data: result } = await api.post(`/v3/project/${id}/upload/complete`, { uploadId: uid });

      // 완료: project 상태와 uploadPhase를 먼저 세팅한 후 step 전환
      setUploadPhase('done');
      setResumeInfo(null);
      setSelectedFile(null);
      setProject(prev => ({ ...prev, originalVideo: { ...result.originalVideo }, status: result.status }));
      setCurrentStep(2);
      addToast('영상 업로드 완료', 'success');
    } catch (err) {
      if (uploadAbortRef.current || err.message === 'cancelled') {
        addToast('업로드가 취소되었습니다. 나중에 이어받기 가능합니다.', 'info');
        setUploadPhase('idle');
      } else {
        addToast(err.response?.data?.error || '업로드 실패: ' + err.message, 'error');
        setUploadPhase('selected');
      }
    }
  };

  const cancelUpload = () => { uploadAbortRef.current = true; };

  const handleDrop = (e) => { e.preventDefault(); handleFileSelect(e.dataTransfer?.files?.[0]); };

  // 대본 파일 선택
  const handleScriptSelect = (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'docx') { addToast('.docx 파일만 업로드 가능합니다', 'error'); return; }
    setSelectedScript(file);
  };

  // 대본 업로드
  const uploadScript = async (file) => {
    const f = file || selectedScript;
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append('script', f, f.name);
      const { data } = await api.post(`/v3/project/${id}/upload/script`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setScriptUploaded(true);
      setScriptPreview(data.preview || '');
      setSelectedScript(null);
      addToast(`대본 업로드 완료 (${data.textLength}자)`, 'success');
    } catch (err) {
      addToast(err.response?.data?.error || '대본 업로드 실패', 'error');
    }
  };

  // 대본 삭제
  const removeScript = async () => {
    try {
      await api.delete(`/v3/project/${id}/upload/script`);
      setScriptUploaded(false);
      setScriptPreview('');
      setSelectedScript(null);
      addToast('대본이 제거되었습니다', 'info');
    } catch (err) {
      addToast('대본 제거 실패', 'error');
    }
  };

  // ─── STEP 2 ───
  const startSTT = async () => {
    setSttRunning(true); setSttProgress({ percent: 0, message: '시작 중...' });
    setSttSegments([]);
    try {
      await runSSE(`/api/v3/project/${id}/stt`, {
        onProgress: (d) => setSttProgress({ percent: d.percent, message: d.message }),
        onSegment: (d) => {
          setSttSegments(prev => [...prev, d]);
          setTimeout(() => sttScrollRef.current?.scrollTo({ top: sttScrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
        },
        onDone: (d) => {
          const tr = (d.transcript || []).map(t => ({ ...t, originalText: t.originalText || t.text, edited: t.edited || false }));
          setTranscript(tr);
          setProject(prev => ({ ...prev, status: 'step3', transcript: tr }));
          if (d.reviewStats) setReviewStats(d.reviewStats);
          if (d.trimStart) setTrimStart(d.trimStart);
          {
            const statsMsg = d.reviewStats ? `, 자동컷 ${d.reviewStats.autoRemoved}개` : '';
            addToast(`STT 완료: ${d.totalSegments}개 구간, NG ${d.ngCount}개 감지${statsMsg}`, 'success');
          }
          setSttRunning(false);
          setSttProgress(null);
          setCurrentStep(3);
        },
        onError: (d) => { addToast('STT 오류: ' + d.message, 'error'); setSttRunning(false); setSttProgress(null); },
      });
    } catch (err) { addToast('STT 실행 실패', 'error'); setSttRunning(false); setSttProgress(null); }
  };

  const handleRerunSTT = async () => {
    if (!window.confirm('대본을 다시 추출하시겠습니까?\n현재 편집 내용은 사라집니다.')) return;
    try {
      await api.patch(`/v3/project/${id}/status`, { status: 'step2_ready' });
      setTranscript([]);
      setSttSegments([]);
      setSttProgress(null);
      setProject(prev => ({ ...prev, status: 'step2_ready', transcript: [] }));
      setCurrentStep(2);
      addToast('STT 재실행 준비 완료', 'info');
    } catch {
      addToast('상태 변경 실패', 'error');
    }
  };

  // ─── STEP 3: 편집기 로직 ───
  const [selectedSegs, setSelectedSegs] = useState(new Set());
  const [undoStack, setUndoStack] = useState([]);
  const [filterReviewOnly, setFilterReviewOnly] = useState(false);
  const autoSaveRef = useRef(null);

  // ── undo ──
  const pushUndo = useCallback(() => {
    setUndoStack(prev => {
      const next = [...prev, JSON.parse(JSON.stringify(transcript))];
      return next.length > 20 ? next.slice(-20) : next;
    });
  }, [transcript]);
  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(u => u.slice(0, -1));
    setTranscript(prev);
    addToast('실행 취소', 'info');
  }, [undoStack, addToast]);

  // ── 기본 함수 ──
  const seekTo = (time, endTime) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
    stopAtRef.current = endTime || null;
    videoRef.current.play().catch(() => {});
  };
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    setCurrentTime(t);
    if (stopAtRef.current !== null && t >= stopAtRef.current) {
      videoRef.current.pause();
      stopAtRef.current = null;
    }
  };
  const togglePlay = () => { if (!videoRef.current) return; videoRef.current.paused ? videoRef.current.play().catch(() => {}) : videoRef.current.pause(); };
  const skip = (seconds) => { if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime + seconds); } };

  const skipFrame = useCallback((frames) => {
    if (!videoRef.current) return;
    const fps = 30;
    const delta = frames / fps;
    const newTime = Math.max(0, Math.min(videoRef.current.currentTime + delta, videoDuration));
    videoRef.current.currentTime = newTime;
    stopAtRef.current = null;
  }, [videoDuration]);

  // ── 세그먼트 편집 ──
  const suppressAutoScroll = (permanent = false) => {
    userInteractingRef.current = true;
    clearTimeout(userInteractingRef._timer);
    if (!permanent) userInteractingRef._timer = setTimeout(() => { userInteractingRef.current = false; }, 3000);
  };
  const resumeAutoScroll = () => { clearTimeout(userInteractingRef._timer); userInteractingRef.current = false; };

  const toggleSegment = (idx) => { pushUndo(); setTranscript(prev => prev.map(t => t.index === idx ? { ...t, selected: !t.selected } : t)); };
  const setSegSelected = (indices, val) => { pushUndo(); setTranscript(prev => prev.map(t => indices.includes(t.index) ? { ...t, selected: val } : t)); };
  const selectAll = () => { pushUndo(); setTranscript(prev => prev.map(t => ({ ...t, selected: true }))); };
  const deleteNGOnly = () => { pushUndo(); setTranscript(prev => prev.map(t => t.ngFlags?.length > 0 ? { ...t, selected: false } : t)); };
  const invertSelection = () => { pushUndo(); setTranscript(prev => prev.map(t => ({ ...t, selected: !t.selected }))); };
  const applyClaudeRecommendation = () => {
    pushUndo();
    setTranscript(prev => prev.map(t => {
      if (t.claudeAction === 'delete') return { ...t, selected: false };
      if (t.claudeAction === 'keep' || t.claudeAction === 'review') return { ...t, selected: true };
      return t;
    }));
    addToast('AI 추천 적용됨', 'success');
  };

  // ── 컷(분할) ──
  const splitAtPlayhead = () => {
    const vTime = videoRef.current?.currentTime ?? currentTime;

    // 현재 시간이 속한 세그먼트 찾기
    let seg = transcript.find(t => vTime >= t.start && vTime < t.end);
    if (!seg) {
      addToast('컷할 구간이 없습니다', 'error');
      return;
    }

    pushUndo();
    const maxIdx = Math.max(...transcript.map(t => t.index));

    const seg1 = {
      ...seg,
      end: vTime,
      words: (seg.words || []).filter(w => w.start < vTime),
      text: (seg.words || []).filter(w => w.start < vTime).map(w => w.word).join(' ') || seg.text,
    };
    const seg2 = {
      ...seg,
      index: maxIdx + 1,
      start: vTime,
      words: (seg.words || []).filter(w => w.start >= vTime),
      text: (seg.words || []).filter(w => w.start >= vTime).map(w => w.word).join(' ') || seg.text,
      originalText: (seg.words || []).filter(w => w.start >= vTime).map(w => w.word).join(' ') || seg.text,
    };

    setTranscript(prev => prev.flatMap(t => t.index === seg.index ? [seg1, seg2] : [t]));
    addToast(`컷 완료 (${formatTime(vTime)})`, 'success');
  };

  // ── 병합 ──
  const mergeSelected = () => {
    const selIdxs = [...selectedSegs].sort((a, b) => {
      const ai = transcript.findIndex(t => t.index === a);
      const bi = transcript.findIndex(t => t.index === b);
      return ai - bi;
    });
    if (selIdxs.length < 2) { addToast('2개 이상 선택해주세요', 'error'); return; }
    pushUndo();
    const segs = selIdxs.map(idx => transcript.find(t => t.index === idx)).filter(Boolean);
    const merged = {
      ...segs[0],
      end: segs[segs.length - 1].end,
      text: segs.map(s => s.text).join(' '),
      words: segs.flatMap(s => s.words || []),
      originalText: segs.map(s => s.originalText || s.text).join(' '),
    };
    setTranscript(prev => {
      const idxSet = new Set(selIdxs.slice(1));
      return prev.map(t => t.index === selIdxs[0] ? merged : t).filter(t => !idxSet.has(t.index));
    });
    setSelectedSegs(new Set([selIdxs[0]]));
    addToast('세그먼트 병합됨', 'success');
  };


  // ── 단어 삭제 (앞뒤 공백 포함) ──
  const toggleDeleteWord = (segIndex, wordStart, wordEnd, wordText) => {
    pushUndo();
    setTranscript(prev => prev.map(t => {
      if (t.index !== segIndex) return t;
      const deleted = t.deletedWords || [];
      const exists = deleted.find(d => Math.abs(d.start - wordStart) < 0.01);
      if (exists) {
        return { ...t, deletedWords: deleted.filter(d => Math.abs(d.start - wordStart) >= 0.01) };
      }
      // 다음 단어의 start까지 포함 (공백 구간 삭제), 마지막 단어면 세그먼트 end까지
      const words = t.words || [];
      const wi = words.findIndex(w => Math.abs(w.start - wordStart) < 0.01);
      const extEnd = wi >= 0 && wi < words.length - 1 ? words[wi + 1].start : t.end;
      return { ...t, deletedWords: [...deleted, { word: wordText, start: wordStart, end: extEnd }] };
    }));
  };

  // ── 통계 ──
  const deletedWordsDuration = transcript.filter(t => t.selected).reduce((sum, t) =>
    sum + (t.deletedWords || []).reduce((s, dw) => s + (dw.end - dw.start), 0), 0);
  const rawSelectedDuration = transcript.filter(t => t.selected).reduce((s, t) => s + (t.end - t.start), 0);
  const selectedDuration = Math.max(0, rawSelectedDuration - deletedWordsDuration);
  const unselectedDuration = transcript.filter(t => !t.selected).reduce((s, t) => s + Math.max(0, t.end - t.start), 0);
  const deletedDuration = unselectedDuration + deletedWordsDuration;
  const totalDuration = rawSelectedDuration + unselectedDuration;

  // ── 타임라인 계산 ──
  const tlClips = useMemo(() => {
    let pos = 0;
    return transcript.map(t => {
      const dur = Math.max(0, t.end - t.start);
      const clip = { index: t.index, sourceStart: t.start, sourceEnd: t.end, timelineStart: pos, duration: dur, text: t.text || '', selected: !!t.selected, deletedWords: t.deletedWords || [] };
      pos += dur;
      return clip;
    });
  }, [transcript]);

  const tlTotalDuration = useMemo(() => {
    if (tlClips.length === 0) return 0;
    const last = tlClips[tlClips.length - 1];
    return last.timelineStart + last.duration;
  }, [tlClips]);

  const tlGetSourceTime = useCallback((tlPos) => {
    const c = tlClips.find(c => tlPos >= c.timelineStart && tlPos < c.timelineStart + c.duration);
    return c ? { index: c.index, sourceTime: c.sourceStart + (tlPos - c.timelineStart) } : null;
  }, [tlClips]);

  const tlGetTimelinePos = useCallback((srcTime) => {
    const c = tlClips.find(c => srcTime >= c.sourceStart && srcTime < c.sourceEnd);
    return c ? c.timelineStart + (srcTime - c.sourceStart) : null;
  }, [tlClips]);

  // ── 타임라인 클릭 ──
  const handleTimelineClick = useCallback((e) => {
    const container = tlScrollRef.current;
    if (!container) return;
    const x = e.clientX - container.getBoundingClientRect().left + container.scrollLeft;
    const sec = x / tlPixelsPerSec;
    const result = tlGetSourceTime(sec);
    if (result && videoRef.current) {
      stopAtRef.current = null;
      videoRef.current.currentTime = result.sourceTime;
      if (e.shiftKey) {
        setSelectedSegs(prev => { const n = new Set(prev); n.has(result.index) ? n.delete(result.index) : n.add(result.index); return n; });
      } else {
        setSelectedSegs(new Set([result.index]));
      }
      setHighlightedSegIndex(result.index);
      const el = listRef.current?.querySelector(`[data-index="${result.index}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [tlPixelsPerSec, tlGetSourceTime]);

  const handleTlSegmentClick = useCallback((e, clip) => {
    e.stopPropagation();
    if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = clip.sourceStart; }
    if (e.shiftKey) {
      setSelectedSegs(prev => { const n = new Set(prev); n.has(clip.index) ? n.delete(clip.index) : n.add(clip.index); return n; });
    } else {
      setSelectedSegs(new Set([clip.index]));
    }
    setHighlightedSegIndex(clip.index);
    const el = listRef.current?.querySelector(`[data-index="${clip.index}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const scrollTimelineToSegment = useCallback((segIndex) => {
    if (!tlScrollRef.current) return;
    const clip = tlClips.find(c => c.index === segIndex);
    if (!clip) return;
    const cx = clip.timelineStart * tlPixelsPerSec + (clip.duration * tlPixelsPerSec) / 2;
    tlScrollRef.current.scrollTo({ left: cx - tlScrollRef.current.clientWidth / 2, behavior: 'smooth' });
  }, [tlClips, tlPixelsPerSec]);

  // ── 파형 데이터 로드 ──
  useEffect(() => {
    if (currentStep !== 3 || waveformRef.current) return;
    api.get(`/v3/project/${id}/waveform`)
      .then(r => {
        waveformRef.current = r.data;
        setWaveformLoaded(true);
      })
      .catch(() => {
        waveformRef.current = null;
        setWaveformLoaded(false);
      });
  }, [currentStep, id, api]);

  // ── Canvas 그리기 ──
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pps = tlPixelsPerSec;
    const totalWidth = tlTotalDuration * pps + 60;
    const h = canvas.height;

    // canvas 크기 동적 설정
    if (canvas.width !== Math.floor(totalWidth)) {
      canvas.width = Math.floor(totalWidth);
    }

    ctx.clearRect(0, 0, canvas.width, h);

    // 배경 전체
    ctx.fillStyle = '#0D1117';
    ctx.fillRect(0, 0, canvas.width, h);

    // 파형 영역 배경 (상단, 검은색)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, h/2);

    // 레이어1: 파형 (상단 절반)
    const waveform = waveformRef.current;
    if (waveform?.peaks) {
      const { peaks, samplesPerSec } = waveform;
      const midY = h / 4;        // 상단 절반의 중앙
      const maxAmp = h * 0.2;    // 진폭
      tlClips.forEach(clip => {
        const startSample = Math.floor(clip.sourceStart * samplesPerSec);
        const endSample = Math.floor(clip.sourceEnd * samplesPerSec);
        const clipColor = clip.selected ? '#4CAF5099' : '#F4433666';
        for (let si = startSample; si < endSample && si < peaks.length; si++) {
          const x = clip.timelineStart * pps + (si - startSample) / samplesPerSec * pps;
          const amp = peaks[si] * maxAmp;
          ctx.fillStyle = clipColor;
          ctx.fillRect(Math.floor(x), midY - amp, 1, amp * 2);
        }
      });
    }

    // 레이어2: 눈금/타임코드 (상단에)
    const step = pps >= 30 ? 5 : 10;
    ctx.strokeStyle = '#2A344155';
    ctx.fillStyle = '#6B728099';
    ctx.font = '8px monospace';
    ctx.lineWidth = 1;
    for (let t = 0; t <= tlTotalDuration + step; t += step) {
      const x = t * pps;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      ctx.fillText(formatTime(t), x + 2, 10);
    }

    // 구분선 (상단/하단 경계)
    ctx.strokeStyle = '#2A3441';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h/2);
    ctx.lineTo(canvas.width, h/2);
    ctx.stroke();

    // 레이어3: 클립 박스 (하단 절반)
    const clipTop = h/2 + 4;
    const clipH = h/2 - 8;

    tlClips.forEach(clip => {
      const left = clip.timelineStart * pps;
      const w = Math.max(clip.duration * pps - 1, 3);
      const isSel = selectedSegs.has(clip.index);
      const isHL = clip.index === highlightedSegIndex;

      ctx.fillStyle = clip.selected ? '#4CAF50cc' : '#F44336aa';
      ctx.fillRect(left, clipTop, w, clipH);

      if (isSel) {
        ctx.strokeStyle = '#2196F3'; ctx.lineWidth = 2;
        ctx.strokeRect(left + 1, clipTop + 1, w - 2, clipH - 2);
      } else if (isHL) {
        ctx.strokeStyle = 'rgba(255,255,255,0.4)'; ctx.lineWidth = 1;
        ctx.strokeRect(left, clipTop, w, clipH);
      }

      // 삭제된 단어
      if (clip.selected && clip.deletedWords?.length > 0) {
        const segDur = clip.sourceEnd - clip.sourceStart;
        if (segDur > 0) {
          clip.deletedWords.forEach(dw => {
            const dwLeft = left + ((dw.start - clip.sourceStart) / segDur) * w;
            const dwW = ((dw.end - dw.start) / segDur) * w;
            ctx.fillStyle = '#F44336cc';
            ctx.fillRect(dwLeft, clipTop, Math.min(dwW, w - (dwLeft - left)), clipH);
          });
        }
      }

      // 텍스트
      if (w > 40) {
        ctx.fillStyle = '#fff';
        ctx.font = '9px sans-serif';
        ctx.save();
        ctx.beginPath();
        ctx.rect(left, clipTop, w, clipH);
        ctx.clip();
        ctx.fillText(clip.text?.slice(0, Math.floor(w / 6)) || '', left + 3, clipTop + clipH - 4);
        ctx.restore();
      }
    });

    // 레이어4: 재생 커서 (전체 높이)
    const cursorPos = tlGetTimelinePos(currentTime);
    if (cursorPos != null) {
      const cx = cursorPos * pps;
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2;
      ctx.shadowColor = 'rgba(255,255,255,0.7)'; ctx.shadowBlur = 6;
      ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, h); ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(cx - 5, 0); ctx.lineTo(cx + 5, 0); ctx.lineTo(cx, 6);
      ctx.fill();
    }
  }, [tlClips, tlTotalDuration, tlPixelsPerSec, selectedSegs, highlightedSegIndex, currentTime, tlGetTimelinePos]);

  // ── Canvas 재렌더링 트리거 ──
  useEffect(() => {
    if (currentStep !== 3) return;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(drawCanvas);
  }, [currentStep, drawCanvas, transcript, selectedSegs, highlightedSegIndex, currentTime, tlPixelsPerSec, waveformLoaded]);

  // ── Canvas 클릭 핸들러 ──
  const handleCanvasClick = useCallback((e) => {
    const scrollEl = tlScrollRef.current;
    if (!scrollEl) return;
    const rect = scrollEl.getBoundingClientRect();
    const x = (e.clientX - rect.left) + scrollEl.scrollLeft;
    const sec = x / tlPixelsPerSec;
    const result = tlGetSourceTime(sec);
    if (result && videoRef.current) {
      stopAtRef.current = null;
      videoRef.current.currentTime = result.sourceTime;
      if (e.shiftKey) {
        setSelectedSegs(prev => {
          const n = new Set(prev);
          n.has(result.index) ? n.delete(result.index) : n.add(result.index);
          return n;
        });
      } else {
        setSelectedSegs(new Set([result.index]));
      }
      setHighlightedSegIndex(result.index);
      const el = listRef.current?.querySelector(`[data-index="${result.index}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [tlPixelsPerSec, tlGetSourceTime]);

  // ── 대사 클릭 ──
  const handleSegmentClick = (t) => {
    if (videoRef.current) {
      stopAtRef.current = null;
      videoRef.current.currentTime = t.start;
    }
    setSelectedSegs(new Set([t.index]));
    setHighlightedSegIndex(t.index);
    scrollTimelineToSegment(t.index);
  };

  // ── 재생 중 자동 스크롤 ──
  useEffect(() => {
    if (userInteractingRef.current || !listRef.current || transcript.length === 0) return;
    const seg = transcript.find(t => currentTime >= t.start && currentTime < t.end);
    if (seg) {
      const el = listRef.current.querySelector(`[data-index="${seg.index}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (isPlaying) setHighlightedSegIndex(seg.index);
    }
  }, [currentTime]);

  // ── 재생 중 타임라인 자동 스크롤 ──
  const tlPlayheadX = useMemo(() => {
    const pos = tlGetTimelinePos(currentTime);
    return pos != null ? pos * tlPixelsPerSec : null;
  }, [currentTime, tlGetTimelinePos, tlPixelsPerSec]);

  useEffect(() => {
    if (!isPlaying || !tlScrollRef.current || tlPlayheadX == null) return;
    const c = tlScrollRef.current;
    if (tlPlayheadX > c.scrollLeft + c.clientWidth * 0.8 || tlPlayheadX < c.scrollLeft) {
      c.scrollTo({ left: tlPlayheadX - c.clientWidth * 0.3, behavior: 'smooth' });
    }
  }, [isPlaying, tlPlayheadX]);

  // ── 키보드 단축키 ──
  useEffect(() => {
    if (currentStep !== 3) return;
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      switch (e.key) {
        case ' ': e.preventDefault(); togglePlay(); break;
        case 'Delete': case 'Backspace':
          if (selectedSegs.size > 0) { e.preventDefault(); setSegSelected([...selectedSegs], false); }
          break;
        case 'z': case 'Z':
          if (e.ctrlKey || e.metaKey) { e.preventDefault(); undo(); }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) skip(-5);
          else skipFrame(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) skip(5);
          else skipFrame(1);
          break;
        case 'j': skip(-5); break;
        case 'k': togglePlay(); break;
        case 'l': skip(5); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentStep, selectedSegs, togglePlay, undo, skip, skipFrame]);

  // ── 자동저장 (30초) ──
  useEffect(() => {
    if (currentStep !== 3 || transcript.length === 0) return;
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      api.patch(`/v3/project/${id}/transcript`, { transcript }).catch(() => {});
    }, 30000);
    return () => clearTimeout(autoSaveRef.current);
  }, [transcript, currentStep]);

  // ─── STEP 4 ───
  const confirmTranscript = async () => {
    const finalTranscript = transcript;
    try {
      await api.patch(`/v3/project/${id}/transcript`, { transcript: finalTranscript });

      // 피드백 학습: AI 추천이 있는 세그먼트에서 사용자가 뒤집은 패턴 수집
      const feedbacks = finalTranscript
        .filter(t => t.claudeAction && (t.claudeAction === 'keep' || t.claudeAction === 'delete'))
        .map(t => ({
          segmentIndex: t.index,
          claudeAction: t.claudeAction,
          userAction: t.selected ? 'keep' : 'delete',
          claudeReason: t.claudeReason || '',
          segmentText: (t.text || '').slice(0, 200),
          ngFlags: t.ngFlags || [],
          segmentDuration: Math.round(((t.end || 0) - (t.start || 0)) * 10) / 10,
        }));
      if (feedbacks.length > 0) {
        api.post(`/v3/project/${id}/feedback`, { feedbacks }).catch(() => {});
      }

      setProject(prev => ({ ...prev, status: 'step4' }));
      addToast('편집 확정 완료. 1차 영상 생성 시작...', 'success');
      startCutEdit();
    } catch { addToast('확정 실패', 'error'); }
  };

  // ─── STEP 5 ───
  const startCutEdit = async () => {
    setCurrentStep(4);
    setCutRunning(true); setCutProgress({ percent: 0, message: '대본 저장 중...' });
    try {
      await api.patch(`/v3/project/${id}/transcript`, { transcript });
      setCutProgress({ percent: 0, message: '시작 중...' });
      await runSSE(`/api/v3/project/${id}/cut-edit`, {
        onProgress: (d) => setCutProgress({ percent: d.percent, message: d.message }),
        onDone: (d) => {
          setProject(prev => ({ ...prev, status: 'step6', editedVideo: d.editedVideo }));
          setCurrentStep(4); addToast('1차 편집 영상 생성 완료!', 'success');
        },
        onError: (d) => { addToast('컷편집 오류: ' + d.message, 'error'); setCurrentStep(3); },
      });
    } catch (err) { addToast('컷편집 실행 실패', 'error'); setCurrentStep(3); }
    finally { setCutRunning(false); setCutProgress(null); }
  };


  // ─── STEP 6 ───
  const goBackToEdit = async () => {
    try { await api.patch(`/v3/project/${id}/status`, { status: 'step3' }); setProject(prev => ({ ...prev, status: 'step3' })); setCurrentStep(3); }
    catch { addToast('상태 변경 실패', 'error'); }
  };

  // ─── STEP 7 ───
  const startStep7 = async () => {
    setCurrentStep(5);
    setStep7Running(true); setStep7Progress({ percent: 0, message: '시작 중...' });
    try {
      await runSSE(`/api/v3/project/${id}/step7`, {
        onProgress: (d) => setStep7Progress({ percent: d.percent, message: d.message }),
        onDone: (d) => {
          if (d.geminiAnalysis) setGeminiAnalysis(d.geminiAnalysis);
          if (d.secondTranscript) setSecondTranscript(d.secondTranscript);
          setProject(prev => ({ ...prev, status: 'step7', geminiAnalysis: d.geminiAnalysis, secondTranscript: d.secondTranscript }));
          setCurrentStep(5);
          const s = d.stats || {};
          addToast(`Gemini 분석 완료: 자막 ${s.subtitles || 0}개, 자료화면 ${s.assets || 0}개, 강조 ${s.highlights || 0}개`, 'success');
        },
        onError: (d) => { addToast('Gemini 분석 오류: ' + d.message, 'error'); setCurrentStep(5); },
      });
    } catch { addToast('영상 분석 실패', 'error'); setCurrentStep(5); }
    finally { setStep7Running(false); setStep7Progress(null); }
  };

  const updateDirective = (segIndex, dirIndex, field, value) => {
    setSecondTranscript(prev => prev.map(seg => {
      if (seg.index !== segIndex) return seg;
      const dirs = [...(seg.directives || [])];
      dirs[dirIndex] = { ...dirs[dirIndex], [field]: value };
      return { ...seg, directives: dirs };
    }));
  };
  const addDirective = (segIndex) => {
    setSecondTranscript(prev => prev.map(seg =>
      seg.index !== segIndex ? seg : { ...seg, directives: [...(seg.directives || []), { type: 'asset', value: '', duration: 3 }] }
    ));
  };
  const removeDirective = (segIndex, dirIndex) => {
    setSecondTranscript(prev => prev.map(seg => {
      if (seg.index !== segIndex) return seg;
      const dirs = [...(seg.directives || [])]; dirs.splice(dirIndex, 1);
      return { ...seg, directives: dirs };
    }));
  };
  const saveSecondTranscript = async () => {
    try {
      if (geminiAnalysis) {
        await api.patch(`/v3/project/${id}/step7`, { geminiAnalysis });
      } else {
        await api.patch(`/v3/project/${id}/step7`, { secondTranscript });
      }
      addToast('저장 완료', 'success');
    } catch { addToast('저장 실패', 'error'); }
  };

  // ─── STEP 8 ───
  const startStep8 = async () => {
    try {
      if (geminiAnalysis) {
        await api.patch(`/v3/project/${id}/step7`, { geminiAnalysis });
      } else {
        await api.patch(`/v3/project/${id}/step7`, { secondTranscript });
      }
    } catch {}
    setCurrentStep(6);
    setStep8Running(true);
    setStep8Progress({ percent: 0, message: '시작 중...', stage: '' });
    setStep8Stages({});
    const stageOrder = ['subtitle', 'assets', 'highlights', 'bgm', 'color', 'intro_outro', 'concat'];
    try {
      await runSSE(`/api/v3/project/${id}/step8`, {
        onProgress: (d) => {
          setStep8Progress({ percent: d.percent, message: d.message, stage: d.stage });
          if (d.stage) {
            setStep8Stages(prev => {
              const updated = { ...prev };
              const idx = stageOrder.indexOf(d.stage);
              stageOrder.forEach((s, i) => {
                if (i < idx) updated[s] = 'done';
                else if (i === idx) updated[s] = 'active';
              });
              return updated;
            });
          }
        },
        onDone: (d) => {
          setProject(prev => ({ ...prev, status: 'step8', finalVideo: d.finalVideo }));
          setCurrentStep(6);
          setStep8Stages(Object.fromEntries(Object.keys(STAGE_LABELS).map(k => [k, 'done'])));
          addToast('최종 영상 생성 완료!', 'success');
        },
        onError: (d) => { addToast('최종 영상 오류: ' + d.message, 'error'); setCurrentStep(5); },
      });
    } catch { addToast('최종 영상 생성 실패', 'error'); setCurrentStep(5); }
    finally { setStep8Running(false); setStep8Progress(null); }
  };

  const goBackToStep6 = async () => {
    try { await api.patch(`/v3/project/${id}/status`, { status: 'step6' }); setProject(prev => ({ ...prev, status: 'step6' })); setCurrentStep(5); }
    catch { addToast('상태 변경 실패', 'error'); }
  };

  const goBackToStep7 = async () => {
    try { await api.patch(`/v3/project/${id}/status`, { status: 'step7' }); setProject(prev => ({ ...prev, status: 'step7' })); setCurrentStep(5); }
    catch { addToast('상태 변경 실패', 'error'); }
  };

  // ─── RENDER ───
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
      <div style={{ width: 36, height: 36, border: '3px solid #2A3441', borderTop: '3px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );
  if (!project) return null;

  // ─── Vertical Step Sidebar ───
  const renderStepSidebar = () => (
    <div style={S.stepSidebar}>
      <button style={S.backBtn} onClick={() => navigate('/v3/project')}>← 목록</button>
      <div style={S.projectName}>{project.name}</div>
      <div style={S.stepList}>
        {STEPS.map((step, i) => {
          const isCompleted = currentStep > step.num;
          const isActive = currentStep === step.num;
          const isComingSoon = step.num >= 5;
          return (
            <div key={step.num} style={{ ...S.stepRow, ...(isComingSoon ? { opacity: 0.35 } : {}) }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  ...S.stepDot,
                  ...(isCompleted && !isComingSoon ? S.stepDotDone : {}),
                  ...(isActive && !isComingSoon ? S.stepDotActive : {}),
                }}>
                  {isCompleted && !isComingSoon ? '✓' : step.num}
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ ...S.stepConnector, backgroundColor: isCompleted && !isComingSoon ? '#4CAF50' : '#2A3441' }} />
                )}
              </div>
              <span style={{
                ...S.stepText,
                color: isComingSoon ? '#6B7280' : isActive ? '#E6EDF3' : isCompleted ? '#4CAF50' : '#6B7280',
                fontWeight: isActive && !isComingSoon ? 600 : 400,
              }}>
                {step.label}{isComingSoon ? ' 🔒' : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Step Content ───
  const activeIndex = transcript.find(t => currentTime >= t.start && currentTime < t.end)?.index;
  const selPercent = totalDuration > 0 ? Math.round((selectedDuration / totalDuration) * 100) : 0;

  const renderContent = () => {
    switch (currentStep) {
      case 1: {
        // 대본 영역 렌더 (우측)
        const renderScriptZone = () => {
          if (scriptUploaded) {
            return (
              <div style={{ ...S.card, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ color: '#4CAF50', fontSize: 20 }}>✅</span>
                  <span style={{ color: '#E6EDF3', fontSize: 15, fontWeight: 600 }}>대본 업로드 완료</span>
                </div>
                <div style={{ background: '#111827', borderRadius: 8, padding: 12, fontSize: 13, color: '#8B949E', maxHeight: 120, overflow: 'hidden', lineHeight: 1.6 }}>
                  {scriptPreview}{scriptPreview.length >= 200 && '...'}
                </div>
                <button style={{ ...S.secondaryBtn, marginTop: 12, fontSize: 12, padding: '6px 14px' }} onClick={removeScript}>대본 제거</button>
              </div>
            );
          }
          if (selectedScript) {
            return (
              <div style={{ ...S.card, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>📄</span>
                  <span style={{ color: '#E6EDF3', fontSize: 14, fontWeight: 600 }}>{selectedScript.name}</span>
                </div>
                <div style={{ color: '#8B949E', fontSize: 13, marginBottom: 12 }}>{formatSize(selectedScript.size)}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...S.primaryBtn, fontSize: 13, padding: '8px 16px' }} onClick={() => uploadScript()}>대본 업로드</button>
                  <button style={{ ...S.secondaryBtn, fontSize: 13, padding: '8px 16px' }} onClick={() => setSelectedScript(null)}>취소</button>
                </div>
              </div>
            );
          }
          return (
            <div
              style={{ ...S.dropzone, flex: 1, padding: '40px 24px', minHeight: 0 }}
              onDrop={e => { e.preventDefault(); handleScriptSelect(e.dataTransfer?.files?.[0]); }}
              onDragOver={e => e.preventDefault()}
              onClick={() => scriptInputRef.current?.click()}
            >
              <input ref={scriptInputRef} type="file" accept=".docx" style={{ display: 'none' }} onChange={e => handleScriptSelect(e.target.files?.[0])} />
              <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.7 }}>📄</div>
              <p style={{ color: '#E6EDF3', fontSize: 14, margin: 0, fontWeight: 500 }}>촬영 대본 (선택사항)</p>
              <p style={{ color: '#6B7280', fontSize: 12, marginTop: 4 }}>.docx 파일</p>
              <p style={{ color: '#6B7280', fontSize: 11, marginTop: 2 }}>대본이 있으면 STT 정확도가 향상됩니다</p>
            </div>
          );
        };

        return (
          <div>
            {/* ── 상태 1: 드롭존 (idle) ── */}
            {uploadPhase === 'idle' && (
              <div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
                  {/* 영상 업로드 (좌측) */}
                  <div style={{ ...S.dropzone, flex: 2, minHeight: 0 }} onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => fileInputRef.current?.click()}>
                    <input ref={fileInputRef} type="file" accept=".mp4,.mov" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files?.[0])} />
                    <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.7 }}>🎬</div>
                    <p style={{ color: '#E6EDF3', fontSize: 16, margin: 0, fontWeight: 500 }}>원본 영상 파일</p>
                    <p style={{ color: '#6B7280', fontSize: 13, marginTop: 6 }}>MP4, MOV / 최대 10GB / 4K 지원</p>
                    <p style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}>드래그하거나 클릭하여 선택</p>
                  </div>
                  {/* 대본 업로드 (우측) */}
                  {renderScriptZone()}
                </div>
                <p style={{ color: '#6B7280', fontSize: 13, marginTop: 12, textAlign: 'center' }}>
                  💡 영상에서 대사를 자동 추출합니다. 대본이 있으면 STT 교정 + AI 편집 정확도가 향상됩니다.
                </p>
              </div>
            )}

            {/* ── 상태 2: 파일 선택됨 ── */}
            {uploadPhase === 'selected' && selectedFile && (
              <div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
                  <div style={{ ...S.card, flex: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                      <span style={{ fontSize: 18 }}>📹</span>
                      <span style={{ color: '#E6EDF3', fontSize: 16, fontWeight: 600 }}>{selectedFile.name}</span>
                    </div>
                    <div style={S.infoGrid}>
                      <div style={S.infoItem}><span style={S.infoLabel}>크기</span><span style={S.infoValue}>{formatSize(selectedFile.size)}</span></div>
                      <div style={S.infoItem}><span style={S.infoLabel}>청크</span><span style={S.infoValue}>{Math.ceil(selectedFile.size / CHUNK_SIZE)}개 (10MB x {Math.ceil(selectedFile.size / CHUNK_SIZE)})</span></div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                      <button style={S.primaryBtn} onClick={() => startUpload(false)}>업로드 시작</button>
                      <button style={S.secondaryBtn} onClick={() => { setSelectedFile(null); setUploadPhase('idle'); }}>다른 파일 선택</button>
                    </div>
                  </div>
                  {renderScriptZone()}
                </div>
              </div>
            )}

            {/* ── 상태 3: 업로드 중 ── */}
            {uploadPhase === 'uploading' && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
                <div style={{ ...S.card, flex: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ fontSize: 18 }}>📤</span>
                    <span style={{ color: '#E6EDF3', fontSize: 16, fontWeight: 600 }}>업로드 중...</span>
                  </div>
                  <div style={{ color: '#C9D1D9', fontSize: 14, marginBottom: 4 }}>{selectedFile?.name}</div>
                  <div style={{ color: '#8B949E', fontSize: 14, marginBottom: 12 }}>
                    {formatSize(uploadStats.uploaded)} / {formatSize(uploadStats.total)}
                  </div>
                  <div style={{ ...S.progressTrack, height: 10, marginBottom: 10 }}>
                    <div style={{ ...S.progressFill, width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#E6EDF3', fontWeight: 700, fontSize: 18 }}>{uploadProgress}%</span>
                    <span style={{ color: '#8B949E' }}>
                      청크 {uploadStats.chunksDone}/{uploadStats.chunksTotal}
                      {uploadStats.speed > 0 && <>&nbsp;&bull;&nbsp;{formatSpeed(uploadStats.speed)}</>}
                    </span>
                  </div>
                  <button style={{ ...S.secondaryBtn, marginTop: 20 }} onClick={cancelUpload}>업로드 취소</button>
                </div>
                {renderScriptZone()}
              </div>
            )}

            {/* ── 상태 4: 이어받기 감지 ── */}
            {uploadPhase === 'resume' && resumeInfo && (
              <div style={{ ...S.card, borderColor: '#FF9800' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 18 }}>⚡</span>
                  <span style={{ color: '#FF9800', fontSize: 16, fontWeight: 600 }}>이전 업로드가 감지되었습니다</span>
                </div>
                <div style={{ color: '#C9D1D9', fontSize: 14, marginBottom: 4 }}>{resumeInfo.filename}</div>
                <div style={{ color: '#8B949E', fontSize: 13, marginBottom: 6 }}>
                  {resumeInfo.uploadedChunks.length}/{resumeInfo.totalChunks} 청크 완료 ({Math.round((resumeInfo.uploadedChunks.length / resumeInfo.totalChunks) * 100)}%)
                </div>
                <div style={{ ...S.progressTrack, height: 6, marginBottom: 16 }}>
                  <div style={{ ...S.progressFill, width: `${Math.round((resumeInfo.uploadedChunks.length / resumeInfo.totalChunks) * 100)}%`, backgroundColor: '#FF9800' }} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ ...S.primaryBtn, cursor: 'pointer', display: 'inline-block' }}>
                    이어서 업로드
                    <input type="file" accept=".mp4,.mov" style={{ display: 'none' }}
                      onChange={e => { const f = e.target.files?.[0]; if (f) startUpload(true, f); }} />
                  </label>
                  <button style={S.secondaryBtn} onClick={() => { setResumeInfo(null); setUploadPhase('idle'); }}>처음부터 다시</button>
                </div>
              </div>
            )}

            {/* ── 상태 5: 서버 합치기 중 ── */}
            {uploadPhase === 'completing' && (
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 20, height: 20, border: '2px solid #2A3441', borderTop: '2px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <span style={{ color: '#E6EDF3', fontSize: 16, fontWeight: 600 }}>파일 처리 중...</span>
                </div>
                <div style={{ color: '#8B949E', fontSize: 14, marginBottom: 12 }}>
                  청크 파일 합치는 중 ({uploadStats.chunksTotal}개)
                </div>
                <div style={{ ...S.progressTrack, height: 10, marginBottom: 10 }}>
                  <div style={{ ...S.progressFill, width: '100%' }} />
                </div>
                <div style={{ color: '#6B7280', fontSize: 13 }}>영상 정보 분석 중...</div>
              </div>
            )}

            {/* ── 상태 6: 업로드 완료 ── */}
            {uploadPhase === 'done' && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
                <div style={{ ...S.card, flex: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ color: '#4CAF50', fontSize: 20 }}>✅</span>
                    <span style={{ color: '#E6EDF3', fontSize: 16, fontWeight: 600 }}>영상 업로드 완료</span>
                  </div>
                  <div style={{ color: '#8B949E', fontSize: 14 }}>
                    {project.originalVideo?.originalName || project.originalVideo?.filename || '영상 파일'}
                  </div>
                </div>
                {renderScriptZone()}
              </div>
            )}
          </div>
        );
      }

      case 2: {
        const ov = project.originalVideo;
        return (
          <div>
            {!sttRunning && (
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ color: '#4CAF50', fontSize: 20 }}>✅</span>
                  <span style={{ color: '#E6EDF3', fontSize: 16, fontWeight: 600 }}>영상 업로드 완료</span>
                </div>
                <div style={{ color: '#C9D1D9', fontSize: 15, marginBottom: 4 }}>
                  {ov?.originalName || ov?.filename || '영상 파일'}
                </div>
                <div style={{ color: '#8B949E', fontSize: 14, marginBottom: 20 }}>
                  길이: {formatDuration(ov?.duration)}&nbsp;&nbsp;•&nbsp;&nbsp;{ov?.resolution || '-'}&nbsp;&nbsp;•&nbsp;&nbsp;{formatFileSize(ov?.size)}
                </div>
                {scriptUploaded && (
                  <div style={{ background: '#4CAF5010', border: '1px solid #4CAF5030', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>📄</span>
                    <span style={{ color: '#4CAF50', fontSize: 13 }}>촬영 대본 첨부됨 — STT 교정 + AI 편집에 활용됩니다</span>
                  </div>
                )}
                <div style={{ borderTop: '1px solid #2A3441', paddingTop: 20, marginBottom: 20 }}>
                  <div style={{ color: '#E6EDF3', fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
                    대사를 추출합니다{scriptUploaded ? ' (대본 참조 모드)' : ''}
                  </div>
                </div>
                <button
                  style={{ ...S.primaryBtn, padding: '14px 32px', fontSize: 16 }}
                  onClick={startSTT}
                >
                  🎙️ 대본 추출 시작
                </button>
              </div>
            )}

            {sttRunning && sttProgress && (
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 18 }}>🎙️</span>
                  <span style={{ color: '#E6EDF3', fontSize: 16, fontWeight: 600 }}>대사 추출 중...</span>
                </div>
                <div style={{ ...S.progressTrack, height: 10, marginBottom: 8 }}>
                  <div style={{ ...S.progressFill, width: `${sttProgress.percent}%`, transition: 'width 0.3s' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <span style={{ color: '#E6EDF3', fontWeight: 700, fontSize: 18 }}>{sttProgress.percent}%</span>
                </div>
                {sttSegments.length > 0 && (
                  <div style={{ borderTop: '1px solid #2A3441', paddingTop: 12 }}>
                    <div style={{ color: '#8B949E', fontSize: 13, marginBottom: 8 }}>추출된 대사 (실시간)</div>
                    <div ref={sttScrollRef} style={{ maxHeight: 240, overflowY: 'auto', background: '#111827', borderRadius: 8, padding: 12 }}>
                      {sttSegments.map((seg, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 13 }}>
                          <span style={{ color: '#4CAF50', fontFamily: 'monospace', flexShrink: 0 }}>{formatTime(seg.start)}</span>
                          <span style={{ color: '#C9D1D9' }}>{seg.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case 3: {
        const pps = tlPixelsPerSec;
        const selSeg = selectedSegs.size > 0 ? transcript.find(t => selectedSegs.has(t.index)) : null;
        // Step3 전용 viewport 고정 레이아웃 — 부모 mainContent padding:32 상쇄
        return (
        <div style={{ position: 'fixed', top: 0, left: 360, right: 0, bottom: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden', background: '#0D1117', zIndex: 50 }}>
          {/* ━━ 좌측 메인 ━━ */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* 1. 컨트롤바 */}
            <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', background: '#161B22', borderBottom: '1px solid #2A3441' }}>
              <button style={S.ctrlBtn} onClick={() => skip(-5)}>◀◀</button>
              <button style={{ ...S.ctrlBtn, fontSize: 10 }} onClick={() => skipFrame(-1)}>◀</button>
              <button style={{ ...S.ctrlBtn, ...S.ctrlBtnPlay }} onClick={togglePlay}>{isPlaying ? '⏸' : '▶'}</button>
              <button style={{ ...S.ctrlBtn, fontSize: 10 }} onClick={() => skipFrame(1)}>▶</button>
              <button style={S.ctrlBtn} onClick={() => skip(5)}>▶▶</button>
              <span style={{ ...S.timeDisplay, marginLeft: 4 }}>{formatTime(currentTime)} / {formatTime(videoDuration)}</span>
              <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 4 }}>
                F{Math.round(currentTime * 30)}
                {(() => {
                  const seg = transcript.find(t => currentTime >= t.start && currentTime < t.end);
                  if (!seg?.words?.length) return null;
                  const snapWord = seg.words.reduce((best, w) =>
                    Math.abs(w.start - currentTime) < Math.abs(best.start - currentTime) ? w : best,
                    seg.words[0]
                  );
                  const snapDiff = Math.abs(snapWord.start - currentTime);
                  return snapDiff > 0.01
                    ? <span style={{ color: '#FFD600' }}> → {formatTime(snapWord.start)}</span>
                    : null;
                })()}
              </span>
              <div style={{ width: 1, height: 16, background: '#2A3441' }} />
              <span style={{ color: '#4CAF50', fontSize: 13, fontWeight: 600 }}>{selPercent}%</span>
              <div style={{ flex: 1 }} />
              <span style={{ color: '#4CAF50', fontSize: 10 }}>■ 살림</span>
              <span style={{ color: '#F44336', fontSize: 10, marginLeft: 4 }}>■ 삭제</span>
              <div style={{ width: 1, height: 16, background: '#2A3441', margin: '0 4px' }} />
            </div>

            {/* 2. 영상 */}
            <div style={{ flex: 1, minHeight: 0, flexShrink: 1, background: '#000', position: 'relative', overflow: 'hidden' }}>
              <video ref={videoRef} src={`/api/v3/project/${id}/video/original`}
                style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => setVideoDuration(videoRef.current?.duration || 0)}
                onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: '#111827', cursor: 'pointer' }}
                onClick={e => { const r = e.currentTarget.getBoundingClientRect(); const p = (e.clientX - r.left) / r.width; if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = p * videoDuration; } }}>
                <div style={{ height: '100%', background: '#4CAF50', width: `${videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0}%`, transition: 'width 0.1s linear' }} />
              </div>
            </div>

            {/* 3. 타임라인 */}
            <div ref={fixedTimelineRef} style={{ height: 120, flexShrink: 0, overflow: 'hidden', background: '#0D1117', borderTop: '1px solid #2A3441' }}>
              <div ref={tlScrollRef} style={{ width: '100%', height: '100%', overflowX: 'auto', overflowY: 'hidden', cursor: 'pointer' }}
                onClick={handleCanvasClick}>
                <canvas
                  ref={canvasRef}
                  width={Math.floor(tlTotalDuration * tlPixelsPerSec + 60)}
                  height={120}
                  style={{ display: 'block', height: '120px' }}
                />
              </div>
            </div>

            {/* 4. 선택구간 */}
            <div style={{ height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', background: '#161B22', borderTop: '1px solid #2A3441', fontSize: 11, color: '#8B949E' }}>
              {selectedSegs.size > 0 ? (
                <>
                  <span style={{ color: '#2196F3', fontWeight: 600 }}>{selectedSegs.size}개 선택</span>
                  <span style={{ color: '#6B7280' }}>|</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selSeg ? `${formatTime(selSeg.start)}~${formatTime(selSeg.end)} ${selSeg.text?.slice(0, 60) || ''}` : ''}
                  </span>
                </>
              ) : <span>클릭하여 세그먼트 선택 (Shift+클릭: 다중선택)</span>}
            </div>

            {/* 5. 편집도구 */}
            <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px', background: '#1E2732', borderTop: '1px solid #2A3441' }}>
              <button style={{ ...S.actionBtn, fontSize: 11, padding: '5px 8px' }} onClick={() => setTlPixelsPerSec(p => Math.max(p - 5, 5))}>🔍-</button>
              <span style={{ fontSize: 10, color: '#6B7280', minWidth: 28, textAlign: 'center' }}>{pps}px</span>
              <button style={{ ...S.actionBtn, fontSize: 11, padding: '5px 8px' }} onClick={() => setTlPixelsPerSec(p => Math.min(p + 5, 200))}>🔍+</button>
              <div style={{ width: 1, height: 20, background: '#2A3441' }} />
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={splitAtPlayhead}>✂️ 컷</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px', color: '#F44336' }} onClick={() => { if (selectedSegs.size > 0) setSegSelected([...selectedSegs], false); }} disabled={selectedSegs.size === 0}>🗑️ 삭제</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px', color: '#4CAF50' }} onClick={() => { if (selectedSegs.size > 0) setSegSelected([...selectedSegs], true); }} disabled={selectedSegs.size === 0}>✅ 살림</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={mergeSelected} disabled={selectedSegs.size < 2}>🔗 병합</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={undo} disabled={undoStack.length === 0}>↩️ 취소</button>
              <div style={{ width: 1, height: 20, background: '#2A3441' }} />
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={() => { const seg = transcript.find(t => currentTime >= t.start && currentTime < t.end); if (seg) seekTo(seg.start, seg.end); }}>▶ 미리보기</button>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: '#6B7280' }}>Space | ←→ 프레임 | Shift+←→ 5초 | Del | Ctrl+Z</span>
            </div>
          </div>

          {/* ━━ 우측 360px ━━ */}
          <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%', borderLeft: '1px solid #2A3441', background: '#161B22', overflow: 'hidden' }}>
            <div style={{ height: 44, flexShrink: 0, display: 'flex', borderBottom: '1px solid #2A3441' }}>
              {[
                { key: 'script', label: '대본', count: transcript.length },
                { key: 'subtitle', label: '자막', count: subtitles.length },
              ].map(tab => (
                <button key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{ flex: 1, background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #4CAF50' : '2px solid transparent', color: activeTab === tab.key ? '#4CAF50' : '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: "'Noto Sans KR', sans-serif" }}>
                  {tab.label}
                  <span style={{ fontSize: 11, opacity: 0.7 }}>({tab.count})</span>
                </button>
              ))}
            </div>

            <div ref={listRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {activeTab === 'script' ? (
                (() => {
                  const filtered = transcript.filter(t => !filterReviewOnly || t.claudeAction === 'review');
                  const items = [];
                  filtered.forEach((t, i) => {
                    if (i > 0) {
                      const prev = filtered[i - 1];
                      const gap = t.start - prev.end;
                      if (gap >= 0.5) {
                        items.push(
                          <div key={`gap-${i}`} style={{
                            padding: '6px 10px',
                            background: '#FFD60015',
                            borderLeft: '3px solid #FFD600',
                            borderBottom: '1px solid #2A344133',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                          }} onClick={() => { if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = prev.end; } }}>
                            <span style={{ fontSize: 14 }}>⚠️</span>
                            <div>
                              <span style={{ fontSize: 11, color: '#FFD600', fontWeight: 600 }}>
                                누락 {gap.toFixed(1)}초
                              </span>
                              <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 6 }}>
                                {formatTime(prev.end)} ~ {formatTime(t.start)}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    }
                    const isActive = t.index === activeIndex;
                    const isSel = selectedSegs.has(t.index);
                    const hasNG = t.ngFlags?.length > 0 && !t.falsePositive;
                    const bc = t.selected ? '#4CAF50' : '#F44336';
                    items.push(
                      <div key={t.index} data-index={t.index} style={{
                        ...S.segItem,
                        backgroundColor: isSel ? 'rgba(33,150,243,0.15)' : isActive ? 'rgba(76,175,80,0.08)' : 'transparent',
                        borderLeft: `3px solid ${isSel ? '#2196F3' : bc}`,
                        transition: 'background-color 0.15s',
                      }}>
                        <button style={{ ...S.toggleBtn, backgroundColor: t.selected ? '#4CAF5020' : '#F4433620', color: t.selected ? '#4CAF50' : '#F44336', width: 22, height: 22, fontSize: 11 }}
                          onClick={() => toggleSegment(t.index)}>{t.selected ? '✓' : '✕'}</button>
                        <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => handleSegmentClick(t)}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
                            <span style={{ fontSize: 10, color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{formatTime(t.start)}~{formatTime(t.end)}</span>
                            {t.edited ? <span style={{ color: '#2196F3', fontSize: 9 }}>수정됨</span> : null}
                          </div>
                            <div>
                              {t.selected && t.words && t.words.length > 0 ? (
                                <div style={{ fontSize: 12, color: '#C9D1D9', lineHeight: 1.5, wordBreak: 'break-all' }}>
                                  {(() => {
                                    const textWords = (t.text || '').split(/\s+/).filter(Boolean);
                                    return textWords.map((tw, wi) => {
                                      const w = t.words[wi];
                                      const isDel = w && (t.deletedWords || []).some(d => Math.abs(d.start - w.start) < 0.01);
                                      return <span key={wi}
                                        onClick={w ? (e => { e.stopPropagation(); toggleDeleteWord(t.index, w.start, w.end, tw); }) : undefined}
                                        style={{ textDecoration: isDel ? 'line-through' : 'none', color: isDel ? '#F44336' : '#C9D1D9', opacity: isDel ? 0.5 : 1, cursor: w ? 'pointer' : 'default' }}
                                        title={w ? `${w.start.toFixed(1)}~${w.end.toFixed(1)}s` : ''}>{tw}{wi < textWords.length - 1 ? ' ' : ''}</span>;
                                    });
                                  })()}
                                </div>
                              ) : <div style={{ fontSize: 12, color: '#C9D1D9', lineHeight: 1.4 }}>{t.text || '(침묵)'}</div>}
                              {(t.autoCut || t.needsReview || hasNG) && (
                                <div style={{ display: 'flex', gap: 3, marginTop: 2, flexWrap: 'wrap' }}>
                                  {t.autoCut && <span style={{ ...S.ngBadge, fontSize: 8, padding: '0 3px', backgroundColor: '#F4433630', color: '#F44336' }}>자동컷</span>}
                                  {t.needsReview && <span style={{ ...S.ngBadge, fontSize: 8, padding: '0 3px', backgroundColor: '#FF980030', color: '#FF9800' }}>확인</span>}
                                  {hasNG && t.ngFlags.map((f, i) => <span key={i} style={{ ...S.ngBadge, fontSize: 8, padding: '0 3px' }}>{f}</span>)}
                                </div>
                              )}
                              {t.claudeAction && t.claudeReason && (
                                <div style={{ fontSize: 9, marginTop: 2, color: t.claudeAction === 'delete' ? '#F44336' : '#4CAF50', opacity: 0.7 }}>🤖 {t.claudeReason}</div>
                              )}
                            </div>
                        </div>
                      </div>
                    );
                  });
                  return items;
                })()
              ) : (
                subtitles.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6B7280', fontSize: 13 }}>
                    <p>자막이 없습니다.</p>
                    <p style={{ fontSize: 12, marginTop: 8 }}>Gemini 분석 후 자동 생성되거나<br/>직접 추가할 수 있습니다.</p>
                    <button
                      onClick={() => {
                        const newSub = { id: Date.now(), start: currentTime, end: currentTime + 3, text: '새 자막' };
                        setSubtitles(prev => [...prev, newSub].sort((a,b) => a.start - b.start));
                      }}
                      style={{ marginTop: 12, padding: '8px 16px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: "'Noto Sans KR', sans-serif" }}>
                      + 자막 추가
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #2A3441' }}>
                      <button
                        onClick={() => {
                          const newSub = { id: Date.now(), start: currentTime, end: currentTime + 3, text: '새 자막' };
                          setSubtitles(prev => [...prev, newSub].sort((a,b) => a.start - b.start));
                        }}
                        style={{ width: '100%', padding: '6px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: "'Noto Sans KR', sans-serif" }}>
                        + 현재 위치에 자막 추가
                      </button>
                    </div>
                    {subtitles.map(sub => (
                      <div key={sub.id}
                        onClick={() => { if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = sub.start; } }}
                        style={{ padding: '8px 10px', borderBottom: '1px solid #2A344133', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: '#6B7280' }}>{formatTime(sub.start)} ~ {formatTime(sub.end)}</span>
                          <button onClick={e => { e.stopPropagation(); setSubtitles(prev => prev.filter(s => s.id !== sub.id)); }}
                            style={{ background: 'none', border: 'none', color: '#F44336', cursor: 'pointer', fontSize: 11, padding: '0 4px' }}>✕</button>
                        </div>
                        <input
                          value={sub.text}
                          onChange={e => setSubtitles(prev => prev.map(s => s.id === sub.id ? { ...s, text: e.target.value } : s))}
                          onClick={e => e.stopPropagation()}
                          style={{ background: '#111827', border: '1px solid #374151', borderRadius: 4, color: '#E6EDF3', fontSize: 12, padding: '4px 8px', width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>

            <div style={{ flexShrink: 0, padding: '8px 10px', borderTop: '1px solid #2A3441', display: 'flex', flexDirection: 'column', gap: 6, background: '#1E2732' }}>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0' }} onClick={selectAll}>전체</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0' }} onClick={deleteNGOnly}>NG삭제</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0' }} onClick={invertSelection}>반전</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0', color: '#2196F3' }} onClick={applyClaudeRecommendation}>AI</button>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0', color: filterReviewOnly ? '#FF9800' : '#8B949E' }} onClick={() => setFilterReviewOnly(prev => !prev)}>{filterReviewOnly ? '전체' : '확인만'}</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0', color: '#FF9800' }} onClick={handleRerunSTT}>STT</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8B949E', padding: '2px 0' }}>
                <span style={{ color: '#4CAF50' }}>살림 {formatDuration(selectedDuration)}</span>
                <span style={{ color: '#F44336' }}>삭제 {formatDuration(deletedDuration)}</span>
              </div>
              <button style={{ ...S.primaryBtn, width: '100%', padding: '12px', fontSize: 14 }} onClick={async () => { await confirmTranscript(); }}>1차 영상 생성 →</button>
            </div>
          </div>
        </div>
        );
      }

      case 4: {
        if (cutRunning && cutProgress) {
          return (
            <div style={S.centerCard}>
              <h3 style={S.cardTitle2}>1차 영상 생성 중</h3>
              <div style={{ ...S.progressTrack, height: 10, marginTop: 20 }}>
                <div style={{ ...S.progressFill, width: `${cutProgress.percent}%` }} />
              </div>
              <p style={{ color: '#8B949E', fontSize: 14, marginTop: 10 }}>{cutProgress.message} ({cutProgress.percent}%)</p>
            </div>
          );
        }
        // 1차 편집 완료 — Step 3과 동일 레이아웃 + 컨트롤바에 다운로드/재생성 버튼
        const pps4 = tlPixelsPerSec;
        const selSeg4 = selectedSegs.size > 0 ? transcript.find(t => selectedSegs.has(t.index)) : null;
        return (
        <div style={{ position: 'fixed', top: 0, left: 360, right: 0, bottom: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden', background: '#0D1117', zIndex: 50 }}>
          {/* ━━ 좌측 메인 ━━ */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* 1. 컨트롤바 */}
            <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', background: '#161B22', borderBottom: '1px solid #2A3441' }}>
              <button style={S.ctrlBtn} onClick={() => skip(-5)}>◀◀</button>
              <button style={{ ...S.ctrlBtn, fontSize: 10 }} onClick={() => skipFrame(-1)}>◀</button>
              <button style={{ ...S.ctrlBtn, ...S.ctrlBtnPlay }} onClick={togglePlay}>{isPlaying ? '⏸' : '▶'}</button>
              <button style={{ ...S.ctrlBtn, fontSize: 10 }} onClick={() => skipFrame(1)}>▶</button>
              <button style={S.ctrlBtn} onClick={() => skip(5)}>▶▶</button>
              <span style={{ ...S.timeDisplay, marginLeft: 4 }}>{formatTime(currentTime)} / {formatTime(videoDuration)}</span>
              <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 4 }}>
                F{Math.round(currentTime * 30)}
                {(() => {
                  const seg = transcript.find(t => currentTime >= t.start && currentTime < t.end);
                  if (!seg?.words?.length) return null;
                  const snapWord = seg.words.reduce((best, w) =>
                    Math.abs(w.start - currentTime) < Math.abs(best.start - currentTime) ? w : best,
                    seg.words[0]
                  );
                  const snapDiff = Math.abs(snapWord.start - currentTime);
                  return snapDiff > 0.01
                    ? <span style={{ color: '#FFD600' }}> → {formatTime(snapWord.start)}</span>
                    : null;
                })()}
              </span>
              <div style={{ width: 1, height: 16, background: '#2A3441' }} />
              <span style={{ color: '#4CAF50', fontSize: 13, fontWeight: 600 }}>{selPercent}%</span>
              <div style={{ width: 1, height: 16, background: '#2A3441', margin: '0 4px' }} />
              <a href={`/api/v3/project/${id}/video/edited`} download style={{ textDecoration: 'none' }}>
                <button style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#2E7D32', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Noto Sans KR', sans-serif" }}>⬇ 다운로드</button>
              </a>
              <button onClick={async () => { await confirmTranscript(); }} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#1565C0', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Noto Sans KR', sans-serif" }}>🔄 재생성</button>
              <div style={{ flex: 1 }} />
              <span style={{ color: '#4CAF50', fontSize: 10 }}>■ 살림</span>
              <span style={{ color: '#F44336', fontSize: 10, marginLeft: 4 }}>■ 삭제</span>
              <div style={{ width: 1, height: 16, background: '#2A3441', margin: '0 4px' }} />
            </div>

            {/* 2. 영상 */}
            <div style={{ flex: 1, minHeight: 0, flexShrink: 1, background: '#000', position: 'relative', overflow: 'hidden' }}>
              <video ref={videoRef} src={`/api/v3/project/${id}/video/original`}
                style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => setVideoDuration(videoRef.current?.duration || 0)}
                onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: '#111827', cursor: 'pointer' }}
                onClick={e => { const r = e.currentTarget.getBoundingClientRect(); const p = (e.clientX - r.left) / r.width; if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = p * videoDuration; } }}>
                <div style={{ height: '100%', background: '#4CAF50', width: `${videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0}%`, transition: 'width 0.1s linear' }} />
              </div>
            </div>

            {/* 3. 타임라인 */}
            <div ref={fixedTimelineRef} style={{ height: 120, flexShrink: 0, overflow: 'hidden', background: '#0D1117', borderTop: '1px solid #2A3441' }}>
              <div ref={tlScrollRef} style={{ width: '100%', height: '100%', overflowX: 'auto', overflowY: 'hidden', cursor: 'pointer' }}
                onClick={handleCanvasClick}>
                <canvas
                  ref={canvasRef}
                  width={Math.floor(tlTotalDuration * tlPixelsPerSec + 60)}
                  height={120}
                  style={{ display: 'block', height: '120px' }}
                />
              </div>
            </div>

            {/* 4. 선택구간 */}
            <div style={{ height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', background: '#161B22', borderTop: '1px solid #2A3441', fontSize: 11, color: '#8B949E' }}>
              {selectedSegs.size > 0 ? (
                <>
                  <span style={{ color: '#2196F3', fontWeight: 600 }}>{selectedSegs.size}개 선택</span>
                  <span style={{ color: '#6B7280' }}>|</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selSeg4 ? `${formatTime(selSeg4.start)}~${formatTime(selSeg4.end)} ${selSeg4.text?.slice(0, 60) || ''}` : ''}
                  </span>
                </>
              ) : <span>클릭하여 세그먼트 선택 (Shift+클릭: 다중선택)</span>}
            </div>

            {/* 5. 편집도구 */}
            <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px', background: '#1E2732', borderTop: '1px solid #2A3441' }}>
              <button style={{ ...S.actionBtn, fontSize: 11, padding: '5px 8px' }} onClick={() => setTlPixelsPerSec(p => Math.max(p - 5, 5))}>🔍-</button>
              <span style={{ fontSize: 10, color: '#6B7280', minWidth: 28, textAlign: 'center' }}>{pps4}px</span>
              <button style={{ ...S.actionBtn, fontSize: 11, padding: '5px 8px' }} onClick={() => setTlPixelsPerSec(p => Math.min(p + 5, 200))}>🔍+</button>
              <div style={{ width: 1, height: 20, background: '#2A3441' }} />
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={splitAtPlayhead}>✂️ 컷</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px', color: '#F44336' }} onClick={() => { if (selectedSegs.size > 0) setSegSelected([...selectedSegs], false); }} disabled={selectedSegs.size === 0}>🗑️ 삭제</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px', color: '#4CAF50' }} onClick={() => { if (selectedSegs.size > 0) setSegSelected([...selectedSegs], true); }} disabled={selectedSegs.size === 0}>✅ 살림</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={mergeSelected} disabled={selectedSegs.size < 2}>🔗 병합</button>
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={undo} disabled={undoStack.length === 0}>↩️ 취소</button>
              <div style={{ width: 1, height: 20, background: '#2A3441' }} />
              <button style={{ ...S.actionBtn, fontSize: 12, padding: '5px 10px' }} onClick={() => { const seg = transcript.find(t => currentTime >= t.start && currentTime < t.end); if (seg) seekTo(seg.start, seg.end); }}>▶ 미리보기</button>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: '#6B7280' }}>Space | ←→ 프레임 | Shift+←→ 5초 | Del | Ctrl+Z</span>
            </div>
          </div>

          {/* ━━ 우측 360px ━━ */}
          <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%', borderLeft: '1px solid #2A3441', background: '#161B22', overflow: 'hidden' }}>
            <div style={{ height: 44, flexShrink: 0, display: 'flex', borderBottom: '1px solid #2A3441' }}>
              {[
                { key: 'script', label: '대본', count: transcript.length },
                { key: 'subtitle', label: '자막', count: subtitles.length },
              ].map(tab => (
                <button key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{ flex: 1, background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #4CAF50' : '2px solid transparent', color: activeTab === tab.key ? '#4CAF50' : '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: "'Noto Sans KR', sans-serif" }}>
                  {tab.label}
                  <span style={{ fontSize: 11, opacity: 0.7 }}>({tab.count})</span>
                </button>
              ))}
            </div>

            <div ref={listRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {activeTab === 'script' ? (
                (() => {
                  const filtered = transcript.filter(t => !filterReviewOnly || t.claudeAction === 'review');
                  const items = [];
                  filtered.forEach((t, i) => {
                    if (i > 0) {
                      const prev = filtered[i - 1];
                      const gap = t.start - prev.end;
                      if (gap >= 0.5) {
                        items.push(
                          <div key={`gap-${i}`} style={{
                            padding: '6px 10px',
                            background: '#FFD60015',
                            borderLeft: '3px solid #FFD600',
                            borderBottom: '1px solid #2A344133',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                          }} onClick={() => { if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = prev.end; } }}>
                            <span style={{ fontSize: 14 }}>⚠️</span>
                            <div>
                              <span style={{ fontSize: 11, color: '#FFD600', fontWeight: 600 }}>
                                누락 {gap.toFixed(1)}초
                              </span>
                              <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 6 }}>
                                {formatTime(prev.end)} ~ {formatTime(t.start)}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    }
                    const isActive = t.index === activeIndex;
                    const isSel = selectedSegs.has(t.index);
                    const hasNG = t.ngFlags?.length > 0 && !t.falsePositive;
                    const bc = t.selected ? '#4CAF50' : '#F44336';
                    items.push(
                      <div key={t.index} data-index={t.index} style={{
                        ...S.segItem,
                        backgroundColor: isSel ? 'rgba(33,150,243,0.15)' : isActive ? 'rgba(76,175,80,0.08)' : 'transparent',
                        borderLeft: `3px solid ${isSel ? '#2196F3' : bc}`,
                        transition: 'background-color 0.15s',
                      }}>
                        <button style={{ ...S.toggleBtn, backgroundColor: t.selected ? '#4CAF5020' : '#F4433620', color: t.selected ? '#4CAF50' : '#F44336', width: 22, height: 22, fontSize: 11 }}
                          onClick={() => toggleSegment(t.index)}>{t.selected ? '✓' : '✕'}</button>
                        <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => handleSegmentClick(t)}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
                            <span style={{ fontSize: 10, color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{formatTime(t.start)}~{formatTime(t.end)}</span>
                            {t.edited ? <span style={{ color: '#2196F3', fontSize: 9 }}>수정됨</span> : null}
                          </div>
                            <div>
                              {t.selected && t.words && t.words.length > 0 ? (
                                <div style={{ fontSize: 12, color: '#C9D1D9', lineHeight: 1.5, wordBreak: 'break-all' }}>
                                  {(() => {
                                    const textWords = (t.text || '').split(/\s+/).filter(Boolean);
                                    return textWords.map((tw, wi) => {
                                      const w = t.words[wi];
                                      const isDel = w && (t.deletedWords || []).some(d => Math.abs(d.start - w.start) < 0.01);
                                      return <span key={wi}
                                        onClick={w ? (e => { e.stopPropagation(); toggleDeleteWord(t.index, w.start, w.end, tw); }) : undefined}
                                        style={{ textDecoration: isDel ? 'line-through' : 'none', color: isDel ? '#F44336' : '#C9D1D9', opacity: isDel ? 0.5 : 1, cursor: w ? 'pointer' : 'default' }}
                                        title={w ? `${w.start.toFixed(1)}~${w.end.toFixed(1)}s` : ''}>{tw}{wi < textWords.length - 1 ? ' ' : ''}</span>;
                                    });
                                  })()}
                                </div>
                              ) : <div style={{ fontSize: 12, color: '#C9D1D9', lineHeight: 1.4 }}>{t.text || '(침묵)'}</div>}
                              {(t.autoCut || t.needsReview || hasNG) && (
                                <div style={{ display: 'flex', gap: 3, marginTop: 2, flexWrap: 'wrap' }}>
                                  {t.autoCut && <span style={{ ...S.ngBadge, fontSize: 8, padding: '0 3px', backgroundColor: '#F4433630', color: '#F44336' }}>자동컷</span>}
                                  {t.needsReview && <span style={{ ...S.ngBadge, fontSize: 8, padding: '0 3px', backgroundColor: '#FF980030', color: '#FF9800' }}>확인</span>}
                                  {hasNG && t.ngFlags.map((f, i) => <span key={i} style={{ ...S.ngBadge, fontSize: 8, padding: '0 3px' }}>{f}</span>)}
                                </div>
                              )}
                              {t.claudeAction && t.claudeReason && (
                                <div style={{ fontSize: 9, marginTop: 2, color: t.claudeAction === 'delete' ? '#F44336' : '#4CAF50', opacity: 0.7 }}>🤖 {t.claudeReason}</div>
                              )}
                            </div>
                        </div>
                      </div>
                    );
                  });
                  return items;
                })()
              ) : (
                subtitles.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6B7280', fontSize: 13 }}>
                    <p>자막이 없습니다.</p>
                    <p style={{ fontSize: 12, marginTop: 8 }}>Gemini 분석 후 자동 생성되거나<br/>직접 추가할 수 있습니다.</p>
                    <button
                      onClick={() => {
                        const newSub = { id: Date.now(), start: currentTime, end: currentTime + 3, text: '새 자막' };
                        setSubtitles(prev => [...prev, newSub].sort((a,b) => a.start - b.start));
                      }}
                      style={{ marginTop: 12, padding: '8px 16px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: "'Noto Sans KR', sans-serif" }}>
                      + 자막 추가
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #2A3441' }}>
                      <button
                        onClick={() => {
                          const newSub = { id: Date.now(), start: currentTime, end: currentTime + 3, text: '새 자막' };
                          setSubtitles(prev => [...prev, newSub].sort((a,b) => a.start - b.start));
                        }}
                        style={{ width: '100%', padding: '6px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: "'Noto Sans KR', sans-serif" }}>
                        + 현재 위치에 자막 추가
                      </button>
                    </div>
                    {subtitles.map(sub => (
                      <div key={sub.id}
                        onClick={() => { if (videoRef.current) { stopAtRef.current = null; videoRef.current.currentTime = sub.start; } }}
                        style={{ padding: '8px 10px', borderBottom: '1px solid #2A344133', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: '#6B7280' }}>{formatTime(sub.start)} ~ {formatTime(sub.end)}</span>
                          <button onClick={e => { e.stopPropagation(); setSubtitles(prev => prev.filter(s => s.id !== sub.id)); }}
                            style={{ background: 'none', border: 'none', color: '#F44336', cursor: 'pointer', fontSize: 11, padding: '0 4px' }}>✕</button>
                        </div>
                        <input
                          value={sub.text}
                          onChange={e => setSubtitles(prev => prev.map(s => s.id === sub.id ? { ...s, text: e.target.value } : s))}
                          onClick={e => e.stopPropagation()}
                          style={{ background: '#111827', border: '1px solid #374151', borderRadius: 4, color: '#E6EDF3', fontSize: 12, padding: '4px 8px', width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>

            <div style={{ flexShrink: 0, padding: '8px 10px', borderTop: '1px solid #2A3441', display: 'flex', flexDirection: 'column', gap: 6, background: '#1E2732' }}>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0' }} onClick={selectAll}>전체</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0' }} onClick={deleteNGOnly}>NG삭제</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0' }} onClick={invertSelection}>반전</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0', color: '#2196F3' }} onClick={applyClaudeRecommendation}>AI</button>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0', color: filterReviewOnly ? '#FF9800' : '#8B949E' }} onClick={() => setFilterReviewOnly(prev => !prev)}>{filterReviewOnly ? '전체' : '확인만'}</button>
                <button style={{ ...S.actionBtn, flex: 1, fontSize: 11, padding: '5px 0', color: '#FF9800' }} onClick={handleRerunSTT}>STT</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8B949E', padding: '2px 0' }}>
                <span style={{ color: '#4CAF50' }}>살림 {formatDuration(selectedDuration)}</span>
                <span style={{ color: '#F44336' }}>삭제 {formatDuration(deletedDuration)}</span>
              </div>
              <button style={{ ...S.primaryBtn, width: '100%', padding: '12px', fontSize: 14, background: '#1565C0' }} onClick={async () => { await confirmTranscript(); }}>🔄 재생성 →</button>
            </div>
          </div>
        </div>
        );
      }

      case 5: return (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#E6EDF3', marginBottom: 12 }}>Coming Soon</div>
          <div style={{ fontSize: 15, color: '#6B7280', marginBottom: 32 }}>Gemini AI 자막 자동 생성 기능이 곧 출시됩니다</div>
          <button onClick={() => setCurrentStep(4)} style={{ padding: '12px 28px', background: '#2A3441', color: '#E6EDF3', border: '1px solid #3A4451', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: "'Noto Sans KR', sans-serif" }}>← 이전으로</button>
        </div>
      );

      case 6: return (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎬</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#E6EDF3', marginBottom: 12 }}>Coming Soon</div>
          <div style={{ fontSize: 15, color: '#6B7280', marginBottom: 32 }}>최종 영상 자동 생성 기능이 곧 출시됩니다</div>
          <button onClick={() => setCurrentStep(4)} style={{ padding: '12px 28px', background: '#2A3441', color: '#E6EDF3', border: '1px solid #3A4451', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: "'Noto Sans KR', sans-serif" }}>← 이전으로</button>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div style={S.editorLayout}>
      {renderStepSidebar()}
      <div style={S.mainContent}>
        {renderContent()}
      </div>
    </div>
  );
}

// ─── Styles ───
const S = {
  editorLayout: {
    display: 'flex',
    gap: 0,
    margin: '-32px',
    minHeight: 'calc(100vh)',
  },
  stepSidebar: {
    width: 160,
    backgroundColor: '#161B22',
    borderRight: '1px solid #2A3441',
    padding: '16px 8px',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  backBtn: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    backgroundColor: '#2A3441',
    border: 'none',
    borderRadius: 8,
    color: '#8B949E',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
    textAlign: 'left',
    marginBottom: 16,
  },
  projectName: {
    fontSize: 13,
    fontWeight: 700,
    color: '#E6EDF3',
    marginBottom: 20,
    wordBreak: 'break-word',
    lineHeight: 1.3,
    padding: '0 2px',
  },
  stepList: {
    display: 'flex',
    flexDirection: 'column',
  },
  stepRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
  },
  stepDot: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: '#2A3441',
    color: '#6B7280',
    flexShrink: 0,
  },
  stepDotDone: {
    backgroundColor: '#4CAF50',
    color: '#fff',
  },
  stepDotActive: {
    backgroundColor: '#2196F3',
    color: '#fff',
    boxShadow: '0 0 0 3px rgba(33,150,243,0.3)',
  },
  stepConnector: {
    width: 2,
    height: 16,
    marginTop: 3,
    marginBottom: 3,
    borderRadius: 1,
  },
  stepText: {
    fontSize: 11,
    paddingTop: 4,
  },

  mainContent: {
    flex: 1,
    padding: 32,
    minWidth: 0,
    overflowY: 'auto',
  },

  // Cards
  card: {
    backgroundColor: '#1E2732',
    border: '1px solid #2A3441',
    borderRadius: 16,
    padding: 24,
  },
  centerCard: {
    backgroundColor: '#1E2732',
    border: '1px solid #2A3441',
    borderRadius: 16,
    padding: 40,
    maxWidth: 500,
    margin: '40px auto',
  },
  cardTitle2: {
    color: '#E6EDF3',
    fontSize: 18,
    fontWeight: 600,
    margin: '0 0 16px',
  },

  // Controls
  controlBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    backgroundColor: '#161B22',
    borderRadius: '12px 12px 0 0',
    border: '1px solid #2A3441',
    borderBottom: 'none',
  },
  ctrlBtn: {
    padding: '6px 12px',
    backgroundColor: '#2A3441',
    border: 'none',
    borderRadius: 6,
    color: '#E6EDF3',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  ctrlBtnPlay: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontWeight: 700,
    padding: '6px 16px',
  },
  timeDisplay: {
    color: '#8B949E',
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 8,
  },

  // Seek bar
  seekBar: {
    height: 8,
    backgroundColor: '#111827',
    cursor: 'pointer',
    position: 'relative',
    borderLeft: '1px solid #2A3441',
    borderRight: '1px solid #2A3441',
  },
  seekFill: {
    height: '100%',
    backgroundColor: '#4CAF50',
    transition: 'width 0.1s linear',
  },

  // Timeline
  timeline: {
    position: 'relative',
    width: '100%',
    height: 28,
    backgroundColor: '#111827',
    borderRadius: '0 0 8px 8px',
    overflow: 'hidden',
    border: '1px solid #2A3441',
    borderTop: 'none',
    marginBottom: 4,
  },

  // Segment list
  segList: {
    maxHeight: 420,
    overflowY: 'auto',
    border: '1px solid #2A3441',
    borderRadius: 12,
    backgroundColor: '#1E2732',
  },
  segItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderBottom: '1px solid #2A344155',
    transition: 'background-color 0.1s',
  },
  toggleBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  segContent: { flex: 1, cursor: 'pointer', minWidth: 0 },
  segTime: { fontSize: 11, color: '#6B7280', fontVariantNumeric: 'tabular-nums', marginBottom: 2 },
  segText: { fontSize: 14, color: '#E6EDF3', lineHeight: 1.4, wordBreak: 'break-word' },
  ngRow: { display: 'flex', gap: 4, marginTop: 4 },
  ngBadge: { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, backgroundColor: '#F4433618', color: '#F44336', border: '1px solid #F4433633' },

  // Bottom bar
  bottomBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
    padding: '12px 16px',
    backgroundColor: '#1E2732',
    borderRadius: 12,
    border: '1px solid #2A3441',
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '6px 12px',
    backgroundColor: '#2A3441',
    border: 'none',
    borderRadius: 6,
    color: '#C9D1D9',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
  },

  // Summary
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  summaryItem: { display: 'flex', flexDirection: 'column', gap: 4, padding: 16, backgroundColor: '#111827', borderRadius: 10, border: '1px solid #2A3441' },
  summaryLabel: { fontSize: 12, color: '#6B7280' },
  summaryValue: { fontSize: 18, fontWeight: 700, color: '#E6EDF3' },

  // Info
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 },
  infoItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  infoLabel: { color: '#6B7280', fontSize: 12 },
  infoValue: { color: '#E6EDF3', fontSize: 14, fontWeight: 500 },

  // Video
  videoPlayer: { width: '100%', maxHeight: 400, display: 'block', backgroundColor: '#000', borderRadius: 10 },

  // Dropzone
  dropzone: {
    border: '2px dashed #2A3441',
    borderRadius: 16,
    padding: '60px 32px',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#1E2732',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  // Progress
  progressTrack: { width: '100%', height: 6, backgroundColor: '#111827', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: '#4CAF50', borderRadius: 3, transition: 'width 0.3s' },

  // Two-column (STEP 7)
  twoCol: { display: 'flex', gap: 20, minHeight: 400 },
  twoColLeft: { width: '40%', flexShrink: 0 },
  twoColRight: { flex: 1, minWidth: 0 },

  // Directive editor
  dirRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    padding: '6px 10px',
    marginLeft: 0,
    borderRadius: 8,
    border: '1px solid',
  },
  dirSelect: {
    padding: '3px 6px',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: 6,
    fontSize: 12,
    outline: 'none',
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  dirInput: {
    flex: 1,
    padding: '4px 8px',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: 6,
    color: '#E6EDF3',
    fontSize: 12,
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  dirDeleteBtn: {
    width: 22,
    height: 22,
    border: 'none',
    borderRadius: 4,
    backgroundColor: '#F4433618',
    color: '#F44336',
    fontSize: 11,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Buttons
  primaryBtn: {
    padding: '10px 24px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
    transition: 'background-color 0.15s',
  },
  secondaryBtn: {
    padding: '10px 20px',
    backgroundColor: '#2A3441',
    border: 'none',
    borderRadius: 10,
    color: '#8B949E',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
    transition: 'background-color 0.15s',
  },
};
